import { useState, type FormEvent } from 'react';
import { aboutInfo, testimonials, faqData } from '@/mocks/data';
import PageSEO from '@/components/feature/PageSEO';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';

const aboutSchema = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  name: '关于年轻的朋友韩国',
  description: aboutInfo.mission,
  url: 'https://funseoul.github.io/about',
  about: {
    '@type': 'Organization',
    name: aboutInfo.brandName,
    description: aboutInfo.mission,
    foundingDate: aboutInfo.foundingYear,
  },
};

const faqSchemaAbout = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqData.map((faq) => ({
    '@type': 'Question',
    name: faq.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: faq.answer,
    },
  })),
};

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formMsg, setFormMsg] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = (formData.get('phone_alt') as string || '').trim();
    if (honeypot) {
      setFormStatus('success');
      setFormMsg('发送成功！我们会尽快回复你~');
      return;
    }

    setFormStatus('loading');
    try {
      const body = new URLSearchParams();
      formData.forEach((value, key) => {
        if (key !== 'phone_alt' || !(value as string).trim()) {
          body.append(key, value as string);
        }
      });

      const res = await fetch('https://readdy.ai/api/form/d9spd1dlbrt1qcgaj7jg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      const text = await res.text();
      let parsed;
      try { parsed = JSON.parse(text); } catch { parsed = null; }

      if (res.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormMsg('发送成功！我们会尽快回复你~');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || text;
        setFormStatus('error');
        setFormMsg(serverMsg.includes('spam') ? '提交失败，请稍后再试。' : (serverMsg || '发送失败，请稍后再试。'));
      }
    } catch {
      setFormStatus('error');
      setFormMsg('网络错误，请稍后重试。');
    }
  };

  return (
    <>
      <PageSEO
        title="关于我们"
        description="了解年轻的朋友韩国——专为年轻人打造的韩国旅行指南平台。我们的使命、团队故事与联系方式。"
        keywords="年轻的朋友韩国,关于我们,品牌故事,韩国旅行指南,联系我们"
        canonicalUrl="/about"
        schema={{ ...aboutSchema, ...faqSchemaAbout }}
      />
      <ScrollToTop />
      <Navbar />
      <main>
        <div className="pt-24 md:pt-28 pb-10 md:pb-14 px-4 md:px-6 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '关于我们' },
              ]}
            />
            <div className="mt-4">
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-2">
                关于我们
              </h1>
              <p className="text-foreground-500 text-sm md:text-base">
                {aboutInfo.tagline}
              </p>
            </div>
          </div>
        </div>

        <section className="py-14 md:py-20 px-4 md:px-6 bg-background-50">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4">
                我们的故事
              </h2>
              <div className="max-w-2xl mx-auto space-y-4">
                {aboutInfo.story.split('\n\n').map((para, idx) => (
                  <p key={idx} className="text-foreground-600 text-sm md:text-base leading-relaxed">
                    {para}
                  </p>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 md:gap-6 mt-12">
              {aboutInfo.stats.map((stat) => (
                <div
                  key={stat.label}
                  className="text-center p-6 rounded-xl bg-background-100 border border-background-200/60"
                >
                  <div className="font-heading text-2xl md:text-3xl font-bold text-primary-500 mb-1">
                    {stat.value}
                  </div>
                  <div className="text-xs md:text-sm text-foreground-500">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 px-4 md:px-6 bg-background-100">
          <div className="max-w-4xl mx-auto">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 text-center mb-10 md:mb-14">
              用户好评
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
              {testimonials.map((t) => (
                <div
                  key={t.name}
                  className="bg-background-50 rounded-xl p-6 border border-background-200/60 text-center"
                >
                  <div className="w-16 h-16 rounded-full overflow-hidden bg-background-200 mx-auto mb-4">
                    <img
                      src={t.avatar}
                      alt={t.name}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                    "{t.content}"
                  </p>
                  <div>
                    <div className="text-sm font-semibold text-foreground-800">{t.name}</div>
                    <div className="text-xs text-foreground-500">{t.role}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 px-4 md:px-6 bg-background-50" id="contact">
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-14">
              <div>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4">
                  联系我们
                </h2>
                <p className="text-foreground-500 text-sm md:text-base mb-8">
                  有任何问题或建议，欢迎随时联系我们。我们会在24小时内回复。
                </p>
                <div className="space-y-5">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-500 flex-shrink-0">
                      <i className="ri-mail-line text-lg"></i>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground-800">邮箱</div>
                      <div className="text-sm text-foreground-500">{aboutInfo.contact.email}</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 text-accent-500 flex-shrink-0">
                      <i className="ri-wechat-line text-lg"></i>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground-800">微信</div>
                      <div className="text-sm text-foreground-500">{aboutInfo.contact.wechat}</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-500 flex-shrink-0">
                      <i className="ri-weibo-line text-lg"></i>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground-800">微博</div>
                      <div className="text-sm text-foreground-500">{aboutInfo.contact.weibo}</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-500 flex-shrink-0">
                      <i className="ri-chat-smile-2-line text-lg"></i>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground-800">小红书</div>
                      <div className="text-sm text-foreground-500">{aboutInfo.contact.xiaohongshu}</div>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <div className="bg-background-100 rounded-xl p-6 md:p-8 border border-background-200/60">
                  <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-4">
                    发送消息
                  </h3>
                  <form
                    onSubmit={handleSubmit}
                    data-readdy-form
                    className="space-y-4"
                  >
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        你的名字
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        required
                        className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-200/60 text-sm text-foreground-800 placeholder:text-foreground-400 outline-none focus:border-primary-300 transition-colors"
                        placeholder="请输入你的名字"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        邮箱地址
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        required
                        className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-200/60 text-sm text-foreground-800 placeholder:text-foreground-400 outline-none focus:border-primary-300 transition-colors"
                        placeholder="请输入你的邮箱"
                      />
                    </div>
                    <div>
                      <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        消息内容
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        required
                        maxLength={500}
                        rows={4}
                        className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-200/60 text-sm text-foreground-800 placeholder:text-foreground-400 outline-none focus:border-primary-300 transition-colors resize-none"
                        placeholder="请输入你想说的话（最多500字）"
                      />
                    </div>
                    <input
                      type="text"
                      name="phone_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                      className="form-honeypot"
                    />
                    <button
                      type="submit"
                      disabled={formStatus === 'loading'}
                      className="w-full py-3 bg-primary-500 hover:bg-primary-600 text-background-50 font-medium rounded-lg transition-all duration-200 whitespace-nowrap cursor-pointer text-sm disabled:opacity-60"
                    >
                      {formStatus === 'loading' ? '发送中...' : '发送消息'}
                    </button>

                    {formStatus === 'success' && (
                      <p className="text-sm text-green-600 text-center mt-2">{formMsg}</p>
                    )}
                    {formStatus === 'error' && (
                      <p className="text-sm text-red-500 text-center mt-2">{formMsg}</p>
                    )}
                  </form>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}